import { useEffect, useRef, useCallback } from 'react';
import EventBus from 'App/services/EventBus';

type AgentRunStarted = {
	runId: number;
	agentId: number;
	// WEB-6800: present so a watcher that only knows a conversationId (e.g. a
	// delegation chip) can correlate live run status.
	conversationId: number | null;
	status: 'running';
};

type AgentRunEventPayload = {
	runId: number;
	seq: number;
	eventType: string;
	// WEB-6800: present so a watcher that subscribed after the run started (and
	// missed agent.runStarted) can still match the event to its conversation.
	conversationId?: number | null;
	payload: Record<string, unknown> | null;
};

type AgentRunFinalized = {
	runId: number;
	status: 'succeeded' | 'failed' | 'cancelled' | 'timed_out';
	conversationId: number | null;
	error: string | null;
};

type Handlers = {
	onRunStarted?: (data: AgentRunStarted) => void;
	onRunEvent?: (data: AgentRunEventPayload) => void;
	onRunFinalized?: (data: AgentRunFinalized) => void;
};

const useAgentRunEvents = (handlers: Handlers): void => {
	const ref = useRef(handlers);
	ref.current = handlers;

	const onStarted = useCallback((_e: unknown, data: AgentRunStarted) => {
		ref.current.onRunStarted?.(data);
	}, []);

	const onEvent = useCallback((_e: unknown, data: AgentRunEventPayload) => {
		ref.current.onRunEvent?.(data);
	}, []);

	const onFinalized = useCallback((_e: unknown, data: AgentRunFinalized) => {
		ref.current.onRunFinalized?.(data);
	}, []);

	useEffect(() => {
		// Through the bus rather than `Tools.$rootScope`: it never throws when Angular has not
		// attached yet, it queues the subscription instead (WEB-7608). The V2 prospecting chat
		// mounts this too (WEB-12859), and a throw there took the whole workspace down.
		const unsubs = [
			EventBus.on('userPrivate.agent.runStarted', onStarted),
			EventBus.on('userPrivate.agent.runEvent', onEvent),
			EventBus.on('userPrivate.agent.runFinalized', onFinalized)
		];
		return () => unsubs.forEach(fn => fn());
	}, [onStarted, onEvent, onFinalized]);
};

export default useAgentRunEvents;
export type { AgentRunStarted, AgentRunEventPayload, AgentRunFinalized };
